import React, { useState } from 'react';
import type { VanLifePlayer, VanLifePark } from '../../../../games/van-life/van-life';
import { PARK_ADJACENCY, ZONE_CARDS, CHALLENGE_CARDS } from '../../../../games/van-life/van-life';

const PARKS = [
  { id: 'denali',     name: 'Denali',             abbr: 'DNL', x: 480, y: 330 },
  { id: 'wrangell',   name: 'Wrangell-St. Elias', abbr: 'WSE', x: 654, y: 407 },
  { id: 'kenai',      name: 'Kenai Fjords',        abbr: 'KNF', x: 496, y: 443 },
  { id: 'glacier',    name: 'Glacier Bay',         abbr: 'GCB', x: 757, y: 487 },
  { id: 'katmai',     name: 'Katmai',              abbr: 'KAT', x: 390, y: 490 },
  { id: 'lake-clark', name: 'Lake Clark',          abbr: 'LKC', x: 420, y: 413 },
  { id: 'kobuk',      name: 'Kobuk Valley',        abbr: 'KBK', x: 303, y: 197 },
  { id: 'gates',      name: 'Gates of the Arctic', abbr: 'GOA', x: 422, y: 180 },
];

const ROUTES = [
  { from: 'kobuk',      to: 'gates'      },
  { from: 'gates',      to: 'denali'     },
  { from: 'kobuk',      to: 'denali'     },
  { from: 'denali',     to: 'lake-clark' },
  { from: 'denali',     to: 'kenai'      },
  { from: 'denali',     to: 'wrangell'   },
  { from: 'lake-clark', to: 'katmai'     },
  { from: 'lake-clark', to: 'kenai'      },
  { from: 'kenai',      to: 'wrangell'   },
  { from: 'wrangell',   to: 'glacier'    },
  // New routes
  { from: 'kobuk',      to: 'lake-clark' },
  { from: 'katmai',     to: 'kenai'      },
  { from: 'katmai',     to: 'glacier'    },
  { from: 'kenai',      to: 'glacier'    },
];

const ALASKA_PATH = `
  M 351,63 L 511,90 L 676,123 L 676,440 L 726,457 L 777,507
  L 890,592 L 839,541 L 757,473 L 695,457 L 634,457 L 531,447
  L 502,437 L 459,453 L 492,400 L 420,413 L 390,490 L 336,573
  L 224,607 L 244,540 L 230,490 L 162,457 L 152,390 L 121,253
  L 234,207 L 224,173 Z
`;

// Tracker geometry
const CELL_W = 70, CELL_H = 32, CELL_GAP = 4;
const TRACKER_W = 7 * CELL_W + 6 * CELL_GAP; // 514
const TRACKER_X = (1000 - TRACKER_W) / 2;     // 243
const TRACKER_Y = 693;
const TRACKER_BG_Y = 685;
const TRACKER_BG_H = 48;

interface Props {
  parks: VanLifePark[];
  players: VanLifePlayer[];
  myId: string;
  movableParkIds?: string[];
  onSelectPark?: (parkId: string) => void;
  tracker?: number;       // 1-7 current position
  p1Name?: string;        // shown left of tracker
  p2Name?: string;        // shown right of tracker
  matchNumber?: number;   // current match (1-5)
}

export default function VanLifeMap({ parks, players, myId, movableParkIds, onSelectPark, tracker, p1Name, p2Name, matchNumber }: Props) {
  const [hovered, setHovered] = useState<string | null>(null);

  const getPark = (id: string) => parks.find(p => p.id === id)!;
  const getCoords = (id: string) => PARKS.find(p => p.id === id)!;

  const hoveredPark = hovered ? getPark(hovered) : null;
  const hoveredZone = hoveredPark ? ZONE_CARDS.find(z => z.id === hoveredPark.zoneCardId) : null;
  const hoveredChallenge = hoveredPark ? CHALLENGE_CARDS.find(c => c.id === hoveredPark.challengeCardId) : null;

  const isMovable = (id: string) => movableParkIds?.includes(id) ?? false;

  const showTracker = tracker !== undefined;

  return (
    <div className="relative w-full">
      <svg
        viewBox="0 0 1000 760"
        className="w-full"
        style={{ fontFamily: 'Georgia, serif' }}
      >
        <defs>
          <filter id="vl-paper" x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" result="noise" />
            <feColorMatrix type="saturate" values="0" in="noise" result="gray" />
            <feBlend in="SourceGraphic" in2="gray" mode="multiply" result="blend" />
            <feComposite in="blend" in2="SourceGraphic" operator="in" />
          </filter>
          <radialGradient id="vl-vignette" cx="50%" cy="50%" r="70%">
            <stop offset="60%" stopColor="transparent" />
            <stop offset="100%" stopColor="rgba(50,30,10,0.3)" />
          </radialGradient>
        </defs>

        {/* Ocean */}
        <rect width="1000" height="760" fill="#8BBAD4" rx={8} />
        {[...Array(14)].map((_, i) => (
          <line key={i} x1={0} y1={60 + i * 55} x2={1000} y2={60 + i * 55}
            stroke="#7AABC5" strokeWidth={0.5} opacity={0.4} />
        ))}

        {/* Land */}
        <path d={ALASKA_PATH} fill="#D4B878" stroke="#7A5C28" strokeWidth={2.5}
          strokeLinejoin="round" filter="url(#vl-paper)" />

        {/* Routes */}
        {ROUTES.map(route => {
          const a = PARKS.find(p => p.id === route.from)!;
          const b = PARKS.find(p => p.id === route.to)!;
          return (
            <line key={`${route.from}-${route.to}`}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke="#8B6914" strokeWidth={2} strokeOpacity={0.5}
              strokeDasharray="6 4"
            />
          );
        })}

        {/* Park markers */}
        {PARKS.map(coords => {
          const park = getPark(coords.id);
          const movable = isMovable(coords.id);
          const conquered = park?.conquered ?? false;
          const playersHere = players.filter(p => p.currentParkId === coords.id);
          const r = 22;

          return (
            <g key={coords.id}
              onMouseEnter={() => setHovered(coords.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => movable && onSelectPark?.(coords.id)}
              style={{ cursor: movable ? 'pointer' : 'default' }}
            >
              {movable && (
                <circle cx={coords.x} cy={coords.y} r={r + 8}
                  fill="rgba(251,191,36,0.25)" stroke="#FBBF24" strokeWidth={2} />
              )}
              <circle cx={coords.x + 2} cy={coords.y + 2} r={r} fill="rgba(0,0,0,0.3)" />
              <circle cx={coords.x} cy={coords.y} r={r}
                fill={conquered ? '#5D3A1A' : '#F5E6C8'}
                stroke={conquered ? '#FBBF24' : movable ? '#FBBF24' : '#5D3A1A'}
                strokeWidth={conquered || movable ? 2.5 : 1.5}
              />
              {conquered && (
                <text x={coords.x} y={coords.y - 6} textAnchor="middle"
                  dominantBaseline="middle" fontSize={14} fill="#FBBF24">★</text>
              )}
              <text x={coords.x} y={conquered ? coords.y + 7 : coords.y + 1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={7} fontWeight="bold" fontFamily="Georgia, serif"
                fill={conquered ? '#F5E6C8' : '#3D1C02'}>
                {coords.abbr}
              </text>
              {playersHere.map((pl, i) => {
                const ox = playersHere.length === 1 ? 0 : (i === 0 ? -9 : 9);
                return (
                  <g key={pl.id}>
                    <circle cx={coords.x + ox} cy={coords.y - r - 6} r={6}
                      fill={pl.color} stroke="white" strokeWidth={1.5} />
                    {pl.id === myId && (
                      <circle cx={coords.x + ox} cy={coords.y - r - 6} r={4}
                        fill="none" stroke="white" strokeWidth={1} />
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Hover tooltip */}
        {hovered && hoveredPark && hoveredZone && hoveredChallenge && (() => {
          const coords = getCoords(hovered);
          const tipX = coords.x > 600 ? coords.x - 170 : coords.x - 10;
          const tipY = coords.y > 400 ? coords.y - 90 : coords.y + 35;
          return (
            <g>
              <rect x={tipX} y={tipY} width={180} height={78} rx={5}
                fill="#3D1C02" fillOpacity={0.95} />
              <text x={tipX + 90} y={tipY + 14} textAnchor="middle"
                fontSize={9} fontWeight="bold" fill="#FBBF24" fontFamily="Georgia, serif">
                {hoveredPark.name}
              </text>
              <text x={tipX + 8} y={tipY + 30} fontSize={8} fill="#F5E6C8" fontFamily="Georgia, serif">
                Zone: {hoveredZone.name}
              </text>
              <foreignObject x={tipX + 8} y={tipY + 36} width={164} height={24}>
                <div style={{ fontSize: 7, color: '#C8A96E', fontFamily: 'Georgia, serif', lineHeight: 1.2 }}>
                  {hoveredZone.description}
                </div>
              </foreignObject>
              <text x={tipX + 8} y={tipY + 57} fontSize={8} fill="#F5E6C8" fontFamily="Georgia, serif">
                Challenge: {hoveredChallenge.name}
              </text>
              <foreignObject x={tipX + 8} y={tipY + 63} width={164} height={16}>
                <div style={{ fontSize: 7, color: '#C8A96E', fontFamily: 'Georgia, serif' }}>
                  {hoveredChallenge.description}
                </div>
              </foreignObject>
            </g>
          );
        })()}

        {/* Vignette + border */}
        <rect width="1000" height="760" fill="url(#vl-vignette)" rx={8} />
        <rect x={4} y={4} width={992} height={752} fill="none" stroke="#5D3A1A" strokeWidth={4} rx={6} />
        <rect x={8} y={8} width={984} height={744} fill="none" stroke="#8B6914" strokeWidth={1.5} rx={4} />

        {/* Title */}
        <rect x={60} y={14} width={200} height={30} rx={4} fill="#5D3A1A" />
        <text x={160} y={34} textAnchor="middle" fontSize={13} fontWeight="bold"
          fontFamily="Georgia, serif" fill="#F5E6C8" letterSpacing={2}>VAN LIFE</text>

        {/* Player legend with roles */}
        {players.map((p, i) => {
          const role = p.playerNumber === 1 ? 'Track' : 'Challenge';
          return (
            <g key={p.id} transform={`translate(820, ${22 + i * 18})`}>
              <circle cx={6} cy={6} r={6} fill={p.color} stroke="white" strokeWidth={1} />
              <text x={16} y={10} fontSize={8} fill="#F5E6C8" fontFamily="Georgia, serif">
                {p.name}{p.id === myId ? ' (you)' : ''} · {role}
              </text>
            </g>
          );
        })}

        {/* Match counter (1–5) */}
        <text x={950} y={64} textAnchor="middle" fontSize={7} fill="#C8A96E"
          fontFamily="Georgia, serif" letterSpacing={1}>MATCH</text>
        {Array.from({ length: 5 }, (_, i) => {
          const m = i + 1;
          const active = m === matchNumber;
          const cy = 70 + i * 27;
          return (
            <g key={m}>
              <rect x={928} y={cy} width={44} height={22} rx={3}
                fill={active ? '#F59E0B' : '#2D1500'}
                stroke={active ? '#FCD34D' : '#5D3A1A'}
                strokeWidth={active ? 2 : 1}
              />
              <text x={950} y={cy + 15} textAnchor="middle"
                fontSize={active ? 13 : 10} fontWeight="bold"
                fill={active ? '#1C0A00' : '#8B6914'}
                fontFamily="Georgia, serif">
                {m}
              </text>
            </g>
          );
        })}

        {/* Tracker strip */}
        {showTracker && (
          <>
            <rect x={40} y={TRACKER_BG_Y} width={920} height={TRACKER_BG_H} rx={5}
              fill="#2D1500" fillOpacity={0.9} stroke="#8B6914" strokeWidth={1} />

            <text x={56} y={TRACKER_Y + CELL_H / 2 + 4} fontSize={8}
              fill="#E74C3C" fontFamily="Georgia, serif">
              ← {p1Name ?? 'P1'}
            </text>

            {Array.from({ length: 7 }, (_, i) => {
              const pos = i + 1;
              const active = pos === tracker;
              const danger = pos === 1 || pos === 7;
              const cx = TRACKER_X + i * (CELL_W + CELL_GAP);
              return (
                <g key={pos}>
                  <rect x={cx} y={TRACKER_Y} width={CELL_W} height={CELL_H} rx={4}
                    fill={active ? '#F59E0B' : danger ? 'rgba(185,28,28,0.25)' : '#3D1C02'}
                    stroke={active ? '#FCD34D' : danger ? '#991B1B' : '#5D3A1A'}
                    strokeWidth={active ? 2 : 1}
                  />
                  <text x={cx + CELL_W / 2} y={TRACKER_Y + CELL_H / 2}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize={active ? 12 : 9} fontWeight="bold"
                    fill={active ? '#1C0A00' : danger ? '#B91C1C' : '#8B6914'}
                    fontFamily="Georgia, serif">
                    {active ? '■' : pos}
                  </text>
                </g>
              );
            })}

            <text x={944} y={TRACKER_Y + CELL_H / 2 + 4} textAnchor="end" fontSize={8}
              fill="#3498DB" fontFamily="Georgia, serif">
              {p2Name ?? 'P2'} →
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
